import { DIFFICULTY_BANDS } from './bands';
import type { DifficultyBand } from './bands';
import type { FixtureCellData, TeamWithFixtures } from '../constants';
import { TEN_POINT_SCALE } from '../constants';
import { getFixtureValue } from '../values';
import type { FixtureValueMetric, VisibleGameweekRange } from '../values';

export type MetricCopy = {
  // Short label for the value column header and the sort control.
  column: string;
  // Card title, with {range} replaced by e.g. "GW2–7".
  title: string;
  caption: string;
  note: string;
};

export const METRIC_COPY: Record<FixtureValueMetric, MetricCopy> = {
  overall: {
    column: 'Difficulty',
    title: 'Overall difficulty over {range}',
    caption:
      "Average difficulty of each team's fixtures on our 1-10 scale. Lower is easier.",
    note: 'Difficulty rates every fixture from 1 (easiest) to 10 (hardest) using our team-strength model, adjusted for venue. The column shows the average across the visible gameweeks; hidden gameweeks are excluded.'
  },
  attack: {
    column: 'Att difficulty',
    title: 'Attacking difficulty over {range}',
    caption:
      "How hard each team's opponents are to score against. Lower is easier.",
    note: "Attacking difficulty rates each fixture 1-10 from the opponent's defensive strength, adjusted for venue. Use it for forwards and attacking midfielders."
  },
  defence: {
    column: 'Def difficulty',
    title: 'Defensive difficulty over {range}',
    caption: 'How much attacking threat each team faces. Lower is easier.',
    note: "Defensive difficulty rates each fixture 1-10 from the opponent's attacking strength, adjusted for venue. Use it for defenders and goalkeepers."
  },
  goals: {
    column: 'xG',
    title: 'Expected goals over {range}',
    caption:
      'Total predicted goals for across the visible gameweeks. Higher is better.',
    note: 'Expected goals is the sum of our predicted goals for in each visible fixture, from the same model that drives the difficulty ratings.'
  },
  cleanSheets: {
    column: 'CS%',
    title: 'Clean-sheet probability over {range}',
    caption:
      'Average chance of a clean sheet across the visible gameweeks. Higher is better.',
    note: 'Clean-sheet probability is the modelled chance of conceding no goals in each visible fixture, averaged over the range.'
  }
};

export const formatGameweekRange = (
  gameweekStart: number,
  gameweekEnd: number
): string =>
  gameweekStart === gameweekEnd
    ? `GW${gameweekStart}`
    : `GW${gameweekStart}–${gameweekEnd}`;

export const getMetricTitle = (
  metric: FixtureValueMetric,
  gameweekStart: number,
  gameweekEnd: number
): string =>
  METRIC_COPY[metric].title.replace(
    '{range}',
    formatGameweekRange(gameweekStart, gameweekEnd)
  );

// Every metric is normalised to a 0-1 "ease" so the sparkline and verdict
// treat them alike: easier fixtures, more goals and likelier clean sheets are
// all "good". Expected goals is capped at a ceiling a very strong side at home
// would reach so the bars have a sensible top.
const GOALS_FOR_CEILING = 3;

export const getFixtureEase = (
  fixture: FixtureCellData,
  metric: FixtureValueMetric
): number | null => {
  const value = getFixtureValue(fixture, metric);
  if (value === null) return null;
  switch (metric) {
    case 'overall':
    case 'attack':
    case 'defence':
      return (TEN_POINT_SCALE - value) / TEN_POINT_SCALE;
    case 'goals':
      return Math.min(value / GOALS_FOR_CEILING, 1);
    case 'cleanSheets':
      return value;
  }
};

export type Verdict = 'strong' | 'average' | 'tough';

const STRONG_RUN_EASE = 0.6;
const AVERAGE_RUN_EASE = 0.4;

export const VERDICT_LABELS: Record<Verdict, string> = {
  strong: 'Strong run',
  average: 'Average run',
  tough: 'Tough run'
};

export const VERDICT_BANDS: Record<Verdict, DifficultyBand> = {
  strong: DIFFICULTY_BANDS['very-easy'],
  average: DIFFICULTY_BANDS.moderate,
  tough: DIFFICULTY_BANDS['very-hard']
};

const isVisible = (
  fixture: FixtureCellData,
  range: VisibleGameweekRange
): boolean =>
  fixture.gameweek >= range.gameweekStart &&
  fixture.gameweek <= range.gameweekEnd &&
  !range.hiddenGameweeks.includes(fixture.gameweek);

export const getRunVerdict = (
  fixtures: FixtureCellData[],
  metric: FixtureValueMetric,
  range: VisibleGameweekRange
): Verdict | null => {
  const eases = fixtures.flatMap((fixture) => {
    if (!isVisible(fixture, range)) return [];
    const ease = getFixtureEase(fixture, metric);
    return ease === null ? [] : [ease];
  });
  if (eases.length === 0) return null;
  const mean = eases.reduce((sum, ease) => sum + ease, 0) / eases.length;
  if (mean >= STRONG_RUN_EASE) return 'strong';
  if (mean >= AVERAGE_RUN_EASE) return 'average';
  return 'tough';
};

// One bar per gameweek column. A double gameweek averages its legs; a blank or
// hidden gameweek has no bar.
export type SparklineBar = {
  gameweek: number;
  ease: number | null;
  band: DifficultyBand | null;
};

export const getSparklineBars = (
  gameweeks: { gameweek: number; fixtures: FixtureCellData[] }[],
  metric: FixtureValueMetric,
  hiddenGameweeks: number[],
  getBand: (fixture: FixtureCellData) => DifficultyBand
): SparklineBar[] =>
  gameweeks.map(({ gameweek, fixtures }) => {
    const eases = hiddenGameweeks.includes(gameweek)
      ? []
      : fixtures.flatMap((fixture) => {
          const ease = getFixtureEase(fixture, metric);
          return ease === null ? [] : [ease];
        });
    if (eases.length === 0) return { gameweek, ease: null, band: null };
    const [firstFixture] = fixtures;
    return {
      gameweek,
      ease: eases.reduce((sum, ease) => sum + ease, 0) / eases.length,
      band: getBand(firstFixture)
    };
  });

export const getRunMeta = (
  fixtures: FixtureCellData[],
  range: VisibleGameweekRange
): string => {
  const visible = fixtures.filter((fixture) => isVisible(fixture, range));
  const homeCount = visible.filter(
    (fixture) => fixture.venue === 'home'
  ).length;
  const awayCount = visible.length - homeCount;
  const legsByGameweek = visible.reduce<Record<number, number>>(
    (counts, fixture) => ({
      ...counts,
      [fixture.gameweek]: (counts[fixture.gameweek] ?? 0) + 1
    }),
    {}
  );
  const doubleCount = Object.values(legsByGameweek).filter(
    (legs) => legs > 1
  ).length;

  if (doubleCount > 0) {
    return `${homeCount} home · ${doubleCount} double`;
  }
  return `${homeCount} home · ${awayCount} away`;
};

const KICKOFF_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/London'
});

// The earliest confirmed kickoff in each gameweek across every team, shown
// under the gameweek number in the column header.
export const getGameweekKickoffDates = (
  teams: TeamWithFixtures[],
  gameweekNumbers: number[]
): Record<number, string | null> => {
  const earliest: Record<number, number> = {};
  teams.forEach((team) => {
    team.fixtures.forEach((fixture) => {
      if (!fixture.kickoffAt) return;
      const time = new Date(fixture.kickoffAt).getTime();
      if (Number.isNaN(time)) return;
      const current = earliest[fixture.gameweek];
      if (current === undefined || time < current) {
        earliest[fixture.gameweek] = time;
      }
    });
  });

  return Object.fromEntries(
    gameweekNumbers.map((gameweek) => [
      gameweek,
      earliest[gameweek] === undefined
        ? null
        : KICKOFF_DATE_FORMAT.format(new Date(earliest[gameweek]))
    ])
  );
};
